import { Flex } from '@iheartjane/reefer';
import type { QualifyingStatusEnum } from '@iheartjane/types';

import { repeatByCount } from '../../../utils/specials/repeatByCount';
import styles from '../specialDetailStatus.module.css';
import type { SpecialProduct } from '../specialDetailStatus.types';
import { SpecialDetailStatusCTA } from '../specialDetailStatusCTA/specialDetailStatusCTA';
import { ProductRow } from './productRow';
import type { ProductRowProps } from './productRow';
import { ProgressBar } from './progressBar';

/** Props for the bundle progress tracker showing qualified and discounted products */
export interface SpecialDetailStatusBundleProgressProps {
  /** Whether qualifiers can also receive the discount */
  allowQualifiersToBeDiscounted: boolean;
  /** Secondary text for the CTA callout */
  calloutSubtext?: string;
  /** Primary text for the CTA callout (e.g., "Add up to 2 more products") */
  calloutText?: string;
  /** Products that receive the discount */
  discountedProducts: SpecialProduct[];
  /** Whether this is a Jane Gold special */
  isGold: boolean;
  /** Callback fired when a product's remove button is clicked */
  onRemoveProduct: ProductRowProps['onRemoveProduct'];
  /** Whether to display the progress bar in grams instead of currency */
  progressInGrams?: boolean;
  /** Products that qualify toward the special */
  qualifiedProducts: SpecialProduct[];
  /** Total value/weight of qualified products for the progress bar */
  qualifiedProductsTotal?: number;
  /** Whether to show a spend/weight progress bar */
  qualifyBySpendAmount?: boolean;
  /** Current qualifying status of the special */
  qualifyingStatus: QualifyingStatusEnum;
  /** Target amount needed to qualify */
  qualifyingTargetAmount?: number;
  /** Discount tag label displayed on product rows (e.g., "$5 back") */
  tagLabel: string;
}

export const SpecialDetailStatusBundleProgress = ({
  allowQualifiersToBeDiscounted,
  calloutSubtext,
  calloutText,
  discountedProducts,
  isGold,
  onRemoveProduct,
  progressInGrams = false,
  qualifiedProducts,
  qualifiedProductsTotal,
  qualifyBySpendAmount,
  qualifyingStatus,
  qualifyingTargetAmount,
  tagLabel,
}: SpecialDetailStatusBundleProgressProps) => {
  const isApplied = qualifyingStatus === 'applied';
  const isQualified = qualifyingStatus === 'qualified';
  const isUnlocked = isQualified || isApplied;

  const qualifiedProductsToRender = repeatByCount(qualifiedProducts);
  const discountedProductsToRender = repeatByCount(discountedProducts ?? []);

  return (
    <Flex
      flexDirection="column"
      gap={16}
      p={24}
      border="transparent-black-ten"
      className={styles['content__border']}
      overflowY="auto"
    >
      {qualifyBySpendAmount && (
        <ProgressBar
          progressInGrams={progressInGrams}
          qualifiedProductsTotal={qualifiedProductsTotal || 0}
          qualifyingTargetAmount={qualifyingTargetAmount || 0}
          unlocked={isUnlocked}
        />
      )}

      {qualifiedProductsToRender.map((product, i) => (
        <ProductRow
          key={`${product.id}-${i}`}
          isDiscounted={allowQualifiersToBeDiscounted}
          isGold={isGold}
          isUnlocked={isUnlocked}
          onRemoveProduct={onRemoveProduct}
          product={product}
          tagLabel={tagLabel}
        />
      ))}

      {isUnlocked &&
        discountedProductsToRender.map((product, i) => (
          <ProductRow
            key={`${product.id}-${i}`}
            isDiscounted
            isGold={isGold}
            isUnlocked
            onRemoveProduct={onRemoveProduct}
            product={product}
            tagLabel={tagLabel}
          />
        ))}
      <div
        aria-live="polite"
        aria-atomic="true"
        className={styles['live__region']}
      >
        {calloutText && (
          <SpecialDetailStatusCTA
            calloutSubtext={calloutSubtext}
            calloutText={calloutText}
            isGold={isGold}
          />
        )}
      </div>
    </Flex>
  );
};
